import React, { useEffect } from "react"; 
import { Button, Table, TableHeader, TableHeaderCell, TableRow, TableCell, TableBody, Loader, Icon, Header } from "semantic-ui-react";
import sortBy from "lodash.sortby";
import { useQuery } from "react-query";
import { getUsers, setDeleteUser, setDemoteUser } from "../queries";
import { UserRoles } from "../auth.ts";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { setAuthorizeUser, setPromoteUser } from "../queries";
import TextAndIcon from "../TextAndIcon.jsx";
import { useAppContext } from "../AppContext.jsx";

const getRoleApprovalStatus = (role) => {
  switch (role) {
    case UserRoles.AUTHORIZED_COMMANDER:
    case UserRoles.AUTHORIZED_DOCTOR:
      return true;
    case UserRoles.UNAUTHORIZED_COMMANDER:
    case UserRoles.UNAUTHORIZED_DOCTOR:
    default:
      return false;
  };
}

const UsersManagementPage = () => {
  const { t, i18n } = useTranslation();

  const getRoleDisplayName = (role) => {
    switch (role) {
      case UserRoles.UNAUTHORIZED_COMMANDER:
      case UserRoles.AUTHORIZED_COMMANDER:
        return (
          <div>
            {t('user')}
          </div>
        );
      case UserRoles.UNAUTHORIZED_DOCTOR:
      case UserRoles.AUTHORIZED_DOCTOR:
        return (
          <div>
            {t('admin')}
            <Icon color="blue" name="doctor" style={{ marginLeft: 5 }} />
          </div>
        );
      default:
        return 'Unknown Role';
    };
  };

  const {
    data: queryData,
    error, // TODO: error
    isLoading,
    refetch: refetchUsers,
  } = useQuery("getUsers", getUsers, {
    onSettled: (d) => dispatch({ type: 'SET_DATA', data: d })
  });


  const { refetchUnauthorizedUserCountRef } = useAppContext();
  
  const refetch = () => {
    refetchUnauthorizedUserCountRef.current();
    refetchUsers();
  }
  
  const setAuthorizeUserMutation = useMutation(setAuthorizeUser, {
    onSuccess: (response) => refetch(),
    // onError
  });

  const setDeleteUserMutation = useMutation(setDeleteUser, {
    onSuccess: (response) => refetch(),
    // onError
  });

  const setPromoteUserMutation = useMutation(setPromoteUser, {
    onSuccess: (response) => refetch(),
    // onError
  });

  const setDeomoteUserMutation = useMutation(setDemoteUser, {
    onSuccess: (response) => refetch(),
    // onError
  });


  const createRow = (rawData, t) => {
    const isApproved = getRoleApprovalStatus(rawData?.role);

    return {
      ...rawData,
      role: getRoleDisplayName(rawData?.role),
      role_raw: rawData?.role,
      creation_time: new Date(rawData?.creation_time).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }),
      approval_status_raw: isApproved,
      approval_status_display: isApproved ? <Icon color='green' name='checkmark' size='large' /> : <Icon color='red' name='delete' size='large' />,
      action_row: <div className="user-management-table-action-row">
        <Button color="green" disabled={isApproved} onClick={() => setAuthorizeUserMutation.mutate({ email: rawData.email })}>
          <TextAndIcon text={t("approve")} icon="user add"/>
        </Button>
        <Button color="red" onClick={() => setDeleteUserMutation.mutate({ email: rawData.email })}>
          <TextAndIcon text={t("remove")} icon="user delete"/>
        </Button>
        { !(rawData?.role === UserRoles.AUTHORIZED_DOCTOR) ? (
          <Button color="blue" onClick={() => setPromoteUserMutation.mutate({email: rawData.email})}>
            <TextAndIcon text={t("promote")} icon="user md"/>
          </Button>
          ):(
            <Button color="purple"  onClick={() => setDeomoteUserMutation.mutate({email: rawData.email})}>
              <TextAndIcon text={t("demote")} icon="user outline"/>
            </Button>
          )
        }
      </div>
    }
  }

  function exampleReducer(state, action) {
    switch (action.type) {
      case 'CHANGE_SORT':
        if (state.column === action.column) {
          return {
            ...state,
            data: state.data.slice().reverse(),
            direction:
              state.direction === 'ascending' ? 'descending' : 'ascending',
          }
        }
  
        return {
          column: action.column,
          data: sortBy(state.data, [action.column]),
          direction: 'ascending',
        }

      case 'SET_DATA':
        return {
          column: null,
          data: (action?.data || []).map((rawData) => createRow(rawData, t)),
          direction: null,
        }
      default:
        break;
    }
  }

  const [state, dispatch] = React.useReducer(exampleReducer, {
    column: null,
    data: null,
    direction: null,
  })

  useEffect(() => {
    if (queryData) dispatch({ type: 'SET_DATA', data: queryData });
  }, [queryData, isLoading]);

  const { column, data, direction } = state

  return (isLoading || !queryData || !data) ? <Loader /> : (
    <div style={{ margin: 'auto auto', width: '100%', height: '100%'}}>
      <Header size="large" textAlign="center">{t("manage-users")}</Header>
      <Table sortable celled striped collapsing unstackable selectable textAlign="center" style={{ width: '100%' }}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell
              sorted={column === 'approval_status_raw' ? direction : null}
              onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'approval_status_raw' })}
            >
              <>{t('approvel-status')}</>
            </TableHeaderCell>
            <TableHeaderCell
              sorted={column === 'role_raw' ? direction : null}
              onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'role_raw' })}
            >
              <>{t('role')}</>
            </TableHeaderCell>
            <TableHeaderCell
              sorted={column === 'course' ? direction : null}
              onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'course' })}
            >
              <>{t('course')}</>
            </TableHeaderCell>
            <TableHeaderCell
              sorted={column === 'phone_number' ? direction : null}
              onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'phone_number' })}
            >
              <>{t('phone')}</>
            </TableHeaderCell>
            <TableHeaderCell
              sorted={column === 'email' ? direction : null}
              onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'email' })}
            >
              <>{t('email')}</>
            </TableHeaderCell>
            <TableHeaderCell
              sorted={column === 'creation_time' ? direction : null}
              onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'creation_time' })}
            >
              <>{t('creation-date')}</>
            </TableHeaderCell>
            <TableHeaderCell
              // sorted={column === 'approval_status_raw' ? direction : null}
              // onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'approval_status_raw' })}
            >
              <>{t('actions')}</>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map(({ email, course, creation_time, role, approval_status_display, action_row, phone_number }) => (
            <TableRow key={email}>
              <TableCell>{approval_status_display}</TableCell>
              <TableCell>{role}</TableCell>
              <TableCell>{course}</TableCell>
              <TableCell>{phone_number}</TableCell>
              <TableCell>{email}</TableCell>
              <TableCell>{creation_time}</TableCell>
              <TableCell collapsing>{action_row}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default UsersManagementPage;
